// Page that shows when logging in to a delinquent org. Allows retrying a charge,
// updating payment info, selecting another saved card, and switching orgs.

import React, { useMemo, useState } from 'react'
import { gql } from '@apollo/client'
import { ApolloConsumer } from '@/components/ApolloConsumer'
import { useMutation, useQuery } from '@apollo/client/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Grid, Alert, Box, Button, Card, Checkbox, Chip, CircularProgress, Container, Dialog, DialogContent, DialogTitle, Divider, FormControlLabel,  IconButton, List, ListItem, ListItemButton, Stack, Typography } from '@mui/material'

// Centralized lodash imports - much cleaner!
import { map } from '@/utils/lodash'

import particles2 from '@/assets/images/hero-bg/particles-2.svg'
import delinquentImage from '@/assets/images/DelinquentImage.png'
// import MarketingHeaders6 from '../../../example-components/MarketingHeaders/MarketingHeaders6';

import { enqueueSnackbar } from 'notistack'
import withSession from '@/housefolios-components/Session/withSession'
import { signOut } from '@/housefolios-components/SignOut'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import { Fragment } from 'react'
import { Close, DeleteOutlined } from '@mui/icons-material'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import PaymentForm from '@/housefolios-components/CreateAccount/PaymentForm'
import CancelPlanButton from '@/housefolios-components/TitleButtons/CancelPlanButton'
import { UPDATE_USER } from '@/housefolios-components/PersonalSettings/mutations'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import CreateNewOrg from '@/housefolios-components/OrganizationSettings/CreateNewOrg'
import { useNavigate } from 'react-router'

const RECHARGE_CARD = gql`
  mutation ($planId: String, $sourceId: String) {
    rechargeCard(planId: $planId, sourceId: $sourceId)
  }
`

const ADD_PAYMENT_METHOD = gql`
  mutation ($token: JSON!, $makeDefault: Boolean) {
    addPaymentMethod(token: $token, makeDefault: $makeDefault)
  }
`

const LIST_CUSTOMER_CARDS = gql`
  query {
    listCustomerCards
  }
`

const DELETE_CUSTOMER_CARD = gql`
  mutation ($sourceId: String!) {
    deleteCustomerCard(sourceId: $sourceId)
  }
`

const SET_CUSTOMER_DEFAULT_PAYMENT = gql`
  mutation ($sourceId: String!) {
    setCustomerDefaultPayment(sourceId: $sourceId)
  }
`

function formatBrand(brand) {
  if (!brand || typeof brand !== 'string') return brand || ''
  return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase()
}

/** Preferred default row from listCustomerCards; falls back to first saved card. */
function pickDefaultCard(cards) {
  if (!cards?.length) return null
  const def = cards.find((c) => c.isDefault)
  return def || cards[0]
}

function formatPaymentSummary(c) {
  if (!c) return ''
  return `${formatBrand(c.brand)} •••• ${c.last4} · Expires ${String(c.exp_month).padStart(2, '0')}/${c.exp_year}`
}

function DelinquentPageComponent(props) {
  const [showSwitch, setShowSwitch] = useState(false)

  const toggleShowSwitch = () => {
    setShowSwitch(!showSwitch)
  }

  const [updateUser] = useMutation(UPDATE_USER)

  const [loader, setLoading] = useState(false)
  const [recovered, setRecovered] = useState(false)
  const navigate = useNavigate()
  const [showPickCard, setShowPickCard] = useState(false)
  const [settingDefaultId, setSettingDefaultId] = useState(null)
  const [showAddPanel, setShowAddPanel] = useState(false)
  const [makeDefaultOnAdd, setMakeDefaultOnAdd] = useState(true)
  const [savingCard, setSavingCard] = useState(false)
  const [addCardError, setAddCardError] = useState('')

  const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY
  const stripePromise = loadStripe(stripePublicKey)
  const { activeOrg } = props.session.me

  const [stripeToken, setStripeToken] = useState('')
  const [resetToken, setResetToken] = useState(() => {
    return null
  })

  const createToken = async (stripe, cardElement, nameOnCard) => {
    if (!nameOnCard) setStripeToken('')
    else {
      let { token } = await stripe.createToken(cardElement, {
        name: nameOnCard })
      setStripeToken(token)
    }
  }

  const [rechargeCard] = useMutation(RECHARGE_CARD)
  const [addPaymentMethod] = useMutation(ADD_PAYMENT_METHOD)
  const { data: cardsData, loading: cardsLoading, refetch: refetchCards } =
    useQuery(LIST_CUSTOMER_CARDS, {
      fetchPolicy: 'network-only'})

  const defaultPaymentCard = useMemo(
    () => pickDefaultCard(cardsData?.listCustomerCards),
    [cardsData?.listCustomerCards],
  )
  const [deleteCustomerCard] = useMutation(DELETE_CUSTOMER_CARD)
  const [setCustomerDefaultPayment] = useMutation(
    SET_CUSTOMER_DEFAULT_PAYMENT,
  )

  const handleMakeDefault = async (sourceId) => {
    setSettingDefaultId(sourceId)
    try {
      const { data } = await setCustomerDefaultPayment({
        variables: { sourceId }})
      if (data?.setCustomerDefaultPayment) {
        await refetchCards()
      }
    } finally {
      setSettingDefaultId(null)
    }
  }

  const closeAddPanel = () => {
    setShowAddPanel(false)
    setAddCardError('')
    if (typeof resetToken === 'function') resetToken()
    setStripeToken('')
  }

  const handleSaveCard = async ({ chargeNow  }) => {
    if (!stripeToken) return
    setAddCardError('')
    setSavingCard(true)
    try {
      const { data } = await addPaymentMethod({
        variables: {
          token: stripeToken,
          makeDefault: makeDefaultOnAdd}})
      const result = data?.addPaymentMethod
      if (!result || result.error || !result.id) {
        setAddCardError(
          (result && result.error) ||
            'Could not save card. Please verify the details and try again.',
        )
        setSavingCard(false)
        return
      }
      await refetchCards()
      if (typeof resetToken === 'function') resetToken()
      setStripeToken('')
      if (chargeNow) {
        setShowAddPanel(false)
        setShowPickCard(false)
        setSavingCard(false)
        handleSubmit(() =>
          rechargeCard({ variables: { sourceId: result.id } }),
        )
        return
      }
      enqueueSnackbar('Card saved.', { variant: 'success'  })
      setShowAddPanel(false)
    } catch (error) {
      setAddCardError(error.message || 'Could not save card.')
    } finally {
      setSavingCard(false)
    }
  }

  const handleSubmit = (mutation) => {
    setLoading(true)
    mutation()
      .then(async ({ data  }) => {
        if (data === null) {
          setLoading(false)
          resetToken()
          return
        }
        if (typeof props.refetch === 'function') {
          await props.refetch()
        }
        setLoading(false)
        setRecovered(true)
      })
      .catch((error) => {
        enqueueSnackbar(`Error: ` + error.message, {
          variant: 'error',
          action: (key) => (
            <Fragment>
              <IconButton
                onClick={() => {
                  props.closeSnackbar(key)
                }}
                size="large"
              >
                <Close style={{ color: 'white' }} />
              </IconButton>
            </Fragment>
          )})
        setLoading(false)
        resetToken()
      })
  }

  if (recovered) {
    return (
      <Box style={{ backgroundColor: "#fff" }} sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        "& > .divider": { zIndex: 7, width: "100%" },
        "& .hero-footer": { zIndex: 7 },
        "& .header-nav-wrapper": { zIndex: 27 },
        "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
        borderTopLeftRadius: "inherit",
        borderTopRightRadius: "inherit",
        alignItems: "center",
        ".MuiCard-root > &": {
          top: -1,
          marginLeft: -1,
          marginRight: -1,
          marginBottom: -2,
          zIndex: 6,
        },
      }}>
        <Container sx={{ py: "3rem" }}>
          <Card sx={{ p: "1.5rem" }}>
            <Typography variant="h4" sx={{ mb: "0.5rem" }}>
              Payment recovered
            </Typography>
            <Typography sx={{ mb: "1rem" }}>
              Your subscription is active again. You can continue using
              Housefolios.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/launchpad')}
            >
              Continue
            </Button>
          </Card>
        </Container>
      </Box>
    )
  }

  return (
    <BlockUi
      blocking={loader}
      loader={
        <PacmanLoader color={'var(--primary)'} loading={true} />
      }
      message={
        <div style={{ color: "#368cff" }}>Attempting New Charge</div>
      }
    >
      {/* <ListItem button onClick={handleOpenEditPortfolio} selected>
        <AttachFileOutlinedIcon />
        Test4
      </ListItem>
      <Dialog
        open={openEditPortfolio}
        onClose={handleCloseEditPortfolio}
        classes={{ paper: 'shadow-lg rounded' }}
        maxWidth="xl"
        fullWidth={true}
      > */}
      <Box sx={[{ backgroundColor: '#fff' }, {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        "& > .divider": { zIndex: 7, width: "100%" },
        "& .hero-footer": { zIndex: 7 },
        "& .header-nav-wrapper": { zIndex: 27 },
        "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
        borderTopLeftRadius: "inherit",
        borderTopRightRadius: "inherit",
        alignItems: "center",
        ".MuiCard-root > &": {
          top: -1,
          marginLeft: -1,
          marginRight: -1,
          marginBottom: -2,
          zIndex: 6,
        },
      }]}>
        <div style={{flexGrow:1,display:"flex",zIndex:6,width:"100%",alignItems:"center"}}>
          <Box style={{ backgroundImage: 'url(' + particles2 + ')' }} sx={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: "100%",
            zIndex: 3,
            opacity: "0.7 !important",
            filter: "grayscale(0%)",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center center",
            borderTopLeftRadius: "inherit",
            borderTopRightRadius: "inherit",
          }} />
          <Box sx={{ position: "relative", zIndex: "7", width: "100%" }}>
            <Box sx={{
              py: "1rem",
              "@media (min-width: 1440px)": { py: "3rem" },
            }}>
              <Container sx={[{ py: "3rem" }, {
                color: "#070919",
                "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
              }]}>
                <Grid container spacing={6}>
                  <Grid size={{xl: 6}}
                    sx={[{ display: "flex" }, { alignItems: "center" }]}
                  >
                    <div>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Box style={{ display: "inline-block", paddingTop: 0, paddingBottom: 0, paddingLeft: "0.85em", paddingRight: "0.85em", fontSize: "70%", fontWeight: 700, lineHeight: "21px", textAlign: "center", whiteSpace: "nowrap", verticalAlign: "baseline", borderRadius: 0, textTransform: "uppercase", height: 20, color: "#fff", backgroundColor: "#f83245" }} sx={[{ height: "auto" }, { borderRadius: "0.65rem !important" }, { py: "0.25rem" }]}>
                          ACCOUNT PAUSED
                        </Box>
                      </div>
                      <Box style={{ marginTop: "1rem" }} sx={{
                        color: "#070919",
                        "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                      }}>
                        <Box component="h1" style={{ marginBottom: "1rem", fontWeight: 700 }} sx={{ fontSize: "3rem", fontWeight: "300", lineHeight: "1.2" }}>
                          Oops! Your Payment Method Didn't Work
                        </Box>
                        <Box component="p" style={{ color: "rgba(59, 62, 102, 0.5)" }} sx={{ fontSize: "1.15rem !important" }}>
                          {defaultPaymentCard?.last4 ? (
                            <>
                              Your payment method ending in{' '}
                              {defaultPaymentCard.last4}{' '}
                              was not successful.
                            </>
                          ) : (
                            <>
                              Your payment method could not be charged
                              successfully.
                            </>
                          )}{' '}
                          Please update your payment information or try
                          recharging your credit card.
                        </Box>
                        {cardsLoading ? (
                          <Box component="p" style={{ color: "rgba(59, 62, 102, 0.5)", marginTop: "1rem", marginBottom: 0 }} sx={{ fontSize: "0.875rem !important" }}>
                            Loading current payment method…
                          </Box>
                        ) : (
                          <Box component="p" style={{ marginTop: "1rem", marginBottom: 0 }} sx={{
                            fontSize: "1.15rem !important",
                            color: "#070919",
                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                          }}>
                            <strong>Current payment method:</strong>{' '}
                            {defaultPaymentCard ? (
                              <span style={{ color: "rgba(59, 62, 102, 0.5)" }}>
                                {formatPaymentSummary(defaultPaymentCard)}
                              </span>
                            ) : (
                              <span style={{ color: "rgba(59, 62, 102, 0.5)" }}>
                                No saved card on file—use Manage Payment Methods
                                to add one.
                              </span>
                            )}
                          </Box>
                        )}
                        <p style={{ color: "rgba(59, 62, 102, 0.5)" }}>
                          <b>Need Help?</b> Use the chat bubble at the
                          bottom right of the screen to contact us!
                        </p>
                        <Box sx={[{
                          background: "#eeeff8",
                          height: "1px",
                          overflow: "hidden",
                          borderColor: "#f4f5fd",
                          my: "1.5rem",
                          width: "25%",
                        }, {
                          borderWidth: "2px !important",
                          borderStyle: "solid !important",
                          opacity: "0.2 !important",
                          borderRadius: "50% !important",
                        }]} />
                        <div>
                          <Button
                            href={null}
                            onClick={() => {
                              setShowPickCard(true)
                            }}
                            size="large"
                            sx={{
                              boxShadow: "0 0.25rem 0.55rem rgba(65, 145, 255, 0.35)",
                              color: "#fff",
                              backgroundColor: "#4191ff",
                              backgroundImage: "none",
                              borderColor: "#4191ff",
                              "&:not(:disabled):hover": { backgroundColor: "#4191ff", color: "#fff" },
                              "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#0e73ff" },
                              "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(65, 145, 255, 0.4), 0 0.0625rem 0.385rem rgba(65, 145, 255, 0.54)" },
                              "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                              display: "block",
                              "@media (min-width: 576px)": { display: "inline-block" },
                            }}
                          >
                            <Box component="span" sx={{
                              display: "inline-block",
                              "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                              "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
                            }}>
                              Manage Payment Methods
                            </Box>
                            <span style={{ display: "inline-flex" }}>
                              <FontAwesomeIcon
                                icon={['fas', 'arrow-right']}
                              />
                            </span>
                          </Button>
                        </div>
                      </Box>
                    </div>
                  </Grid>
                  <Grid size={{xl: 6}}
                    sx={[{
                      px: 0,
                      "@media (min-width: 1440px)": { display: "flex" },
                      alignItems: "center",
                    }, { display: "none" }]}
                  >
                    <Box component="img" src={delinquentImage} style={{ width: "100%", marginLeft: "auto", marginRight: "auto", display: "block" }} alt="..." sx={{ maxWidth: "100%", height: "auto" }} />
                  </Grid>
                </Grid>
              </Container>
            </Box>
          </Box>
        </div>
        <Box sx={[{
          py: "1rem",
          "@media (min-width: 1440px)": { py: "3rem" },
        }, { zIndex: 7 }]}>
          <Container>
            <Grid container spacing={6}>
              <Grid size={{md: 6, xl: 4}}>
                <Card sx={[{
                  transition: "all 0.2s ease-in-out !important",
                  "&:hover": { boxShadow: "0 0.56875rem 3.3875rem rgba(59, 62, 102, 0.05), 0 0.9975rem 2.40625rem rgba(59, 62, 102, 0.07), 0 0.35rem 1.23125rem rgba(59, 62, 102, 0.05), 0 0.225rem 1.1875rem rgba(59, 62, 102, 0.09)", transform: "translateY(0)" },
                  "&.card-box-hover:hover": { boxShadow: "0 0.56875rem 3.3875rem rgba(59, 62, 102, 0.05), 0 0.9975rem 2.40625rem rgba(59, 62, 102, 0.07), 0 0.35rem 1.23125rem rgba(59, 62, 102, 0.05), 0 0.225rem 1.1875rem rgba(59, 62, 102, 0.09)", transform: "translateY(0)" },
                  pb: "1.5rem",
                  backgroundColor: "#f8f9ff",
                }, {
                  textAlign: "center",
                  "& .card-icon-wrapper": {
                    height: "76px !important",
                    lineHeight: "58px !important",
                    width: "76px !important",
                    margin: "-44px auto 24px",
                    border: "#fff solid 6px",
                  },
                  borderTopWidth: 3,
                  borderTopStyle: "solid",
                  boxShadow: "0 0.18rem 0.3rem rgba(166, 166, 185, 0.5), 0 0.326rem 3rem rgba(79, 80, 98, 0.3) !important",
                }, { borderColor: "#f83245", mb: "3rem" }]}>
                  <Box component="h3" style={{ fontWeight: 700, marginBottom: "1.5rem" }} sx={[{ fontSize: "1.15rem !important" }, { mt: "3rem" }]}>
                    Cancel Account
                  </Box>
                  <p style={{ color: "#4f5062", paddingLeft: "1.5rem", paddingRight: "1.5rem", marginBottom: "1.5rem" }}>
                    If you'd like to cancel your account, Click the
                    button below.
                  </p>
                  <CancelPlanButton title />
                  {/* <Button
                    href={null}
                    onClick={toggleCancel}
                    title="Cancel Account">
                    <span>Cancel Account</span>
                  </Button> */}
                </Card>
              </Grid>
              <Grid size={{md: 6, xl: 4}}>
                <Card sx={[{
                  transition: "all 0.2s ease-in-out !important",
                  "&:hover": { boxShadow: "0 0.56875rem 3.3875rem rgba(59, 62, 102, 0.05), 0 0.9975rem 2.40625rem rgba(59, 62, 102, 0.07), 0 0.35rem 1.23125rem rgba(59, 62, 102, 0.05), 0 0.225rem 1.1875rem rgba(59, 62, 102, 0.09)", transform: "translateY(0)" },
                  "&.card-box-hover:hover": { boxShadow: "0 0.56875rem 3.3875rem rgba(59, 62, 102, 0.05), 0 0.9975rem 2.40625rem rgba(59, 62, 102, 0.07), 0 0.35rem 1.23125rem rgba(59, 62, 102, 0.05), 0 0.225rem 1.1875rem rgba(59, 62, 102, 0.09)", transform: "translateY(0)" },
                  pb: "1.5rem",
                  backgroundColor: "#f8f9ff",
                }, {
                  textAlign: "center",
                  "& .card-icon-wrapper": {
                    height: "76px !important",
                    lineHeight: "58px !important",
                    width: "76px !important",
                    margin: "-44px auto 24px",
                    border: "#fff solid 6px",
                  },
                  borderTopWidth: 3,
                  borderTopStyle: "solid",
                  boxShadow: "0 0.18rem 0.3rem rgba(166, 166, 185, 0.5), 0 0.326rem 3rem rgba(79, 80, 98, 0.3) !important",
                }, { borderColor: "#4191ff", mb: "3rem" }]}>
                  <Box component="h3" style={{ fontWeight: 700, marginBottom: "1.5rem" }} sx={[{ fontSize: "1.15rem !important" }, { mt: "3rem" }]}>
                    Sign Out
                  </Box>
                  <p style={{ color: "#4f5062", paddingLeft: "1.5rem", paddingRight: "1.5rem", marginBottom: "1.5rem" }}>
                    You can sign out of your account by clicking the
                    button below.
                  </p>
                  <ApolloConsumer>
                    {(client) => (
                      <Button
                        href={null}
                        onClick={() =>
                          signOut(client, activeOrg.member)
                        }
                        sx={{
                          fontWeight: 400,
                          color: "#3b3e66",
                          textDecoration: "none",
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: "#4191ff",
                            textDecoration: "underline",
                            transform: "none",
                          },
                          "&:active": { textDecoration: "underline", boxShadow: "none" },
                          "&:disabled": { color: "#7a7b97", pointerEvents: "none" },
                          "& span": {
                            position: "relative",
                            "&::before": {
                              content: "\"\"",
                              position: "absolute",
                              width: "100%",
                              height: 2,
                              bottom: 0,
                              left: 0,
                              visibility: "hidden",
                              transform: "scaleX(0)",
                              transition: "all 0.3s ease-in-out 0s",
                            },
                            transition: "all 0.2s ease-in-out",
                            color: "#4191ff",
                          },
                          "&:hover span::before": { visibility: "visible", transform: "scaleX(1)" },
                          "&:disabled, &.disabled": { color: "#d1d2db", pointerEvents: "none" },
                          "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.95) !important", background: "#252525 !important", borderBottomColor: "rgba(255, 255, 255, 0.12) !important" },
                          "& span::before": { backgroundColor: "#4191ff" },
                          "&:hover span": { color: "#0e73ff" },
                          mb: "0.5rem",
                          p: 0,
                        }}
                        title="Sign Out"
                      >
                        <span>Sign Out</span>
                      </Button>
                    )}
                  </ApolloConsumer>
                </Card>
              </Grid>
              <Grid size={{md: 6, xl: 4}} sx={[{
                "@media (min-width: 1440px)": { display: "block" },
              }, { display: "none" }]}>
                <Card sx={[{
                  transition: "all 0.2s ease-in-out !important",
                  "&:hover": { boxShadow: "0 0.56875rem 3.3875rem rgba(59, 62, 102, 0.05), 0 0.9975rem 2.40625rem rgba(59, 62, 102, 0.07), 0 0.35rem 1.23125rem rgba(59, 62, 102, 0.05), 0 0.225rem 1.1875rem rgba(59, 62, 102, 0.09)", transform: "translateY(0)" },
                  "&.card-box-hover:hover": { boxShadow: "0 0.56875rem 3.3875rem rgba(59, 62, 102, 0.05), 0 0.9975rem 2.40625rem rgba(59, 62, 102, 0.07), 0 0.35rem 1.23125rem rgba(59, 62, 102, 0.05), 0 0.225rem 1.1875rem rgba(59, 62, 102, 0.09)", transform: "translateY(0)" },
                  pb: "1.5rem",
                  backgroundColor: "#f8f9ff",
                }, {
                  textAlign: "center",
                  "& .card-icon-wrapper": {
                    height: "76px !important",
                    lineHeight: "58px !important",
                    width: "76px !important",
                    margin: "-44px auto 24px",
                    border: "#fff solid 6px",
                  },
                  borderTopWidth: 3,
                  borderTopStyle: "solid",
                  boxShadow: "0 0.18rem 0.3rem rgba(166, 166, 185, 0.5), 0 0.326rem 3rem rgba(79, 80, 98, 0.3) !important",
                }, { borderColor: "#f4772e", mb: "3rem" }]}>
                  <Box component="h3" style={{ fontWeight: 700, marginBottom: "1.5rem" }} sx={[{ fontSize: "1.15rem !important" }, { mt: "3rem" }]}>
                    Switch Organization
                  </Box>
                  <p style={{ color: "#4f5062", paddingLeft: "1.5rem", paddingRight: "1.5rem", marginBottom: "1.5rem" }}>
                    Switch to another organization by clicking the
                    below button.
                  </p>
                  <Button
                    onClick={toggleShowSwitch}
                    sx={{
                      fontWeight: 400,
                      color: "#3b3e66",
                      textDecoration: "none",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "#4191ff",
                        textDecoration: "underline",
                        transform: "none",
                      },
                      "&:active": { textDecoration: "underline", boxShadow: "none" },
                      "&:disabled": { color: "#7a7b97", pointerEvents: "none" },
                      "& span": {
                        position: "relative",
                        "&::before": {
                          content: "\"\"",
                          position: "absolute",
                          width: "100%",
                          height: 2,
                          bottom: 0,
                          left: 0,
                          visibility: "hidden",
                          transform: "scaleX(0)",
                          transition: "all 0.3s ease-in-out 0s",
                        },
                        transition: "all 0.2s ease-in-out",
                        color: "#4191ff",
                      },
                      "&:hover span::before": { visibility: "visible", transform: "scaleX(1)" },
                      "&:disabled, &.disabled": { color: "#d1d2db", pointerEvents: "none" },
                      "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.95) !important", background: "#252525 !important", borderBottomColor: "rgba(255, 255, 255, 0.12) !important" },
                      "& span::before": { backgroundColor: "#4191ff" },
                      "&:hover span": { color: "#0e73ff" },
                      mb: "0.5rem",
                      p: 0,
                    }}
                    title="Switch Organization"
                  >
                    Switch Organization
                  </Button>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
      <Dialog open={showSwitch} onClose={toggleShowSwitch}>
        <DialogTitle>Switch Organization</DialogTitle>
        <DialogContent>
          <List component="div" sx={{
            paddingTop: 0,
            paddingBottom: 0,
            "& .MuiListItem-root, & .MuiListItemButton-root": {
              width: "100%",
              padding: "0.75rem 1.25rem",
              backgroundColor: "#fff",
              border: "1px solid rgba(0, 0, 0, 0.125)",
              borderRadius: 0,
              margin: 0,
              borderWidth: "0 0 1px",
              "&.MuiListItem-button": {
                borderRadius: 0,
                borderWidth: "0 0 1px",
                "& .MuiTouchRipple-root": { opacity: 0.2 },
              },
              "&.list-group-item-action": { transition: "all 0.2s ease-in-out" },
              "&.MuiListItem-button:hover, &.list-group-item-action:hover": { background: "#f4f5fd", zIndex: 1 },
              "&.MuiListItem-button:active, &.list-group-item-action:active": { color: "#000", backgroundColor: "#eeeff8" },
              "&:first-of-type": { borderTop: "0 !important" },
              "&:last-child": { borderBottom: "0 !important" },
            },
          }}>
            {map(props.session.me.roles, (roleOrg) => {
              if (
                roleOrg.organization &&
                roleOrg.role[0] !== 'deactive' &&
                roleOrg.organization._id !== 'superAdmin'
              ) {
                if (
                  roleOrg.organization._id ===
                  props.session.me.activeOrg._id
                )
                  return (
                    <ListItemButton
                      component="a"
                      key={roleOrg.organization}
                      href={null}
                      disableRipple
                      onClick={(e) => e.preventDefault()}
                      sx={{
                        display: "flex",
                        backgroundColor: "#fff",
                        transition: "all 0.2s ease-in-out !important",
                        transform: "scale(1) !important",
                        boxShadow: "none",
                        position: "relative",
                        zIndex: "0",
                        "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: "1rem",
                      }}
                    >
                      <div
                        style={{ display: "flex", alignItems: "center" }}
                        style={{ width: '100%' }}
                      >
                        <Box style={{ height: 50, width: 50, lineHeight: "50px", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, marginRight: "1rem" }} sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: "0.25rem",
                          position: "relative",
                          fontSize: "1.2rem",
                          transition: "all 0.2s ease-in-out",
                          "&:last-child": { mr: 0, marginRight: 0 },
                          "& .badge": { position: "absolute", right: 0, bottom: 0 },
                          "& svg, & .svg-inline--fa": { display: "block" },
                          marginRight: "0.25rem",
                        }}>
                          <IconButton
                            style={{ backgroundColor: '#28b854' }}
                            size="large"
                          >
                            <FontAwesomeIcon
                              icon={['far', 'building']}
                              style={{ color: 'white' }}
                              size="1x"
                            />
                          </IconButton>
                        </Box>
                        <div>
                          <Box style={{ fontWeight: 700 }} sx={{
                            fontSize: "0.875rem !important",
                            color: "#070919",
                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                          }}>
                            {roleOrg.organization.name}
                          </Box>
                          <Box style={{ display: "flex", alignItems: "center" }} sx={{ fontSize: "0.8333333333rem !important" }}>
                            <Box style={{ display: "inline-block", paddingTop: 0, paddingBottom: 0, paddingLeft: "0.7em", paddingRight: "0.7em", fontSize: "70%", fontWeight: 700, lineHeight: "21px", textAlign: "center", whiteSpace: "nowrap", verticalAlign: "baseline", borderRadius: "100%", textTransform: "uppercase", height: 10, color: "#fff", backgroundColor: "#1bc943", textIndent: "-999rem", width: 10, padding: 0, boxShadow: "0 0 0 2px #fff", borderColor: "#fff", marginRight: "0.5rem" }} sx={{ borderWidth: "1px !important", borderStyle: "solid !important" }}>
                              Completed
                            </Box>
                            <div style={{ color: "#1bc943" }}>
                              Selected
                            </div>
                          </Box>
                        </div>
                        <div style={{ flex: 1 }} />
                        <div
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Chip
                            color="primary"
                            disabled={
                              roleOrg.organization._id ===
                              props.session.me.defaultOrg
                            }
                            label={
                              roleOrg.organization._id ===
                              props.session.me.defaultOrg
                                ? 'Default Org'
                                : 'Make Default'
                            }
                            onClick={(e) => {
                              updateUser({
                                variables: {
                                  userInput: {
                                    defaultOrg:
                                      roleOrg.organization._id}},
                                refetchQueries: [{ query: GET_ME }],
                              }).then(async ({ data  }) => {
                                localStorage.setItem(
                                  'token',
                                  data.updateUser.token,
                                )
                                //window.location.reload()
                              })
                              e.preventDefault()
                            }}
                          />
                        </div>
                      </div>
                    </ListItemButton>
                  )
                else
                  return (
                    <ListItemButton
                      component="a"
                      href={null}
                      disableRipple
                      onClick={(e) => {
                        updateUser({
                          variables: {
                            userInput: {
                              organization: roleOrg.organization._id}},
                          refetchQueries: [
                            { query: GET_ME },
                            { query: GET_PORTFOLIOS },
                          ],
                        }).then(async ({ data  }) => {
                          localStorage.setItem(
                            'token',
                            data.updateUser.token,
                          )
                          window.location.reload()
                        })
                        e.preventDefault()
                      }}
                      sx={{
                        display: "flex",
                        backgroundColor: "#fff",
                        transition: "all 0.2s ease-in-out !important",
                        transform: "scale(1) !important",
                        boxShadow: "none",
                        position: "relative",
                        zIndex: "0",
                        "&:hover": { borderRadius: "0.65rem", transform: "scale(1.08)", zIndex: 3 },
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: "1rem",
                      }}
                    >
                      <div
                        style={{ display: "flex", alignItems: "center" }}
                        style={{ width: '100%' }}
                      >
                        <Box style={{ height: 50, width: 50, lineHeight: "50px", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, marginRight: "1rem" }} sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: "0.25rem",
                          position: "relative",
                          fontSize: "1.2rem",
                          transition: "all 0.2s ease-in-out",
                          "&:last-child": { mr: 0, marginRight: 0 },
                          "& .badge": { position: "absolute", right: 0, bottom: 0 },
                          "& svg, & .svg-inline--fa": { display: "block" },
                          marginRight: "0.25rem",
                        }}>
                          <IconButton
                            style={{ backgroundColor: '#797d95' }}
                            size="large"
                          >
                            <FontAwesomeIcon
                              icon={['far', 'building']}
                              style={{ color: 'white' }}
                              // size="md"
                            />
                          </IconButton>
                        </Box>
                        <div>
                          <Box style={{ fontWeight: 700 }} sx={{
                            fontSize: "0.875rem !important",
                            color: "#070919",
                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                          }}>
                            {roleOrg.organization.name}
                          </Box>
                          <Box style={{ display: "flex", alignItems: "center" }} sx={{ fontSize: "0.8333333333rem !important" }}>
                            <Box style={{ backgroundColor: '#797d95' }} style={{ display: "inline-block", paddingTop: 0, paddingBottom: 0, paddingLeft: "0.7em", paddingRight: "0.7em", fontSize: "70%", fontWeight: 700, lineHeight: "21px", textAlign: "center", whiteSpace: "nowrap", verticalAlign: "baseline", borderRadius: "100%", textTransform: "uppercase", height: 10, textIndent: "-999rem", width: 10, padding: 0, boxShadow: "0 0 0 2px #fff", borderColor: "#fff", marginRight: "0.5rem" }} sx={{ borderWidth: "1px !important", borderStyle: "solid !important" }}>
                              Completed
                            </Box>
                            <div style={{ color: '#797d95' }}>
                              Not Selected
                            </div>
                          </Box>
                        </div>
                        <div style={{ flex: 1 }} />
                        <div
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Chip
                            color="primary"
                            disabled={
                              roleOrg.organization._id ===
                              props.session.me.defaultOrg
                            }
                            label={
                              roleOrg.organization._id ===
                              props.session.me.defaultOrg
                                ? 'Default Org'
                                : 'Make Default'
                            }
                            onClick={(e) => {
                              updateUser({
                                variables: {
                                  userInput: {
                                    defaultOrg:
                                      roleOrg.organization._id}},
                                refetchQueries: [{ query: GET_ME }],
                              }).then(async ({ data  }) => {
                                localStorage.setItem(
                                  'token',
                                  data.updateUser.token,
                                )
                                //window.location.reload()
                              })
                              e.preventDefault()
                            }}
                          />
                        </div>
                      </div>
                    </ListItemButton>
                  )
              }
            })}
            <CreateNewOrg listItem />
          </List>
        </DialogContent>
      </Dialog>
      <Dialog
        open={showPickCard}
        onClose={() => {
          setShowPickCard(false)
          closeAddPanel()
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Payment methods</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
              mt: -0.5
            }}>
            Recharge a saved card to retry your payment, change which card we
            bill first, or add a new card. The current default cannot be
            deleted until another card is default.
          </Typography>
          {cardsLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 4}}
            >
              <CircularProgress size={32} />
            </Box>
          ) : (
            <List disablePadding>
              {(cardsData?.listCustomerCards || []).map((c) => (
                <ListItem key={c.id} disablePadding sx={{ mb: 2 }}>
                  <Stack
                    spacing={1.5}
                    sx={{
                      width: '100%',
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2}}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        justifyContent: "space-between",
                        alignItems: "flex-start"
                      }}>
                      <Box>
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          sx={{
                            alignItems: "center",
                            flexWrap: "wrap"
                          }}>
                          <Typography variant="subtitle2" component="span">
                            {c.brand} •••• {c.last4}
                          </Typography>
                          {c.isDefault ? (
                            <Chip
                              size="small"
                              label="Default"
                              color="primary"
                            />
                          ) : null}
                        </Stack>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            mt: 0.5
                          }}>
                          Expires {String(c.exp_month).padStart(2, '0')}/
                          {c.exp_year}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      useFlexGap
                      sx={{
                        alignItems: { xs: 'stretch', sm: 'center' },
                        flexWrap: "wrap"
                      }}>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          boxShadow: "0 0.25rem 0.55rem rgba(65, 145, 255, 0.35)",
                          color: "#fff",
                          backgroundColor: "#4191ff",
                          backgroundImage: "none",
                          borderColor: "#4191ff",
                          "&:not(:disabled):hover": { backgroundColor: "#4191ff", color: "#fff" },
                          "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#0e73ff" },
                          "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(65, 145, 255, 0.4), 0 0.0625rem 0.385rem rgba(65, 145, 255, 0.54)" },
                          "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                        }}
                        onClick={() => {
                          setShowPickCard(false)
                          handleSubmit(() =>
                            rechargeCard({
                              variables: { sourceId: c.id }}),
                          )
                        }}
                      >
                        Recharge
                      </Button>
                      {!c.isDefault ? (
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={settingDefaultId === c.id}
                          onClick={() => handleMakeDefault(c.id)}
                        >
                          Make default
                        </Button>
                      ) : null}
                      <IconButton
                        size="small"
                        aria-label="Remove card"
                        color="error"
                        disabled={c.isDefault}
                        sx={{
                          alignSelf: { xs: 'flex-start', sm: 'center' }}}
                        onClick={async () => {
                          try {
                            const { data } = await deleteCustomerCard({
                              variables: { sourceId: c.id }})
                            if (data?.deleteCustomerCard) {
                              await refetchCards()
                            }
                          } catch (_) {
                            /* Apollo surfaces GraphQL errors */
                          }
                        }}
                      >
                        <DeleteOutlined />
                      </IconButton>
                    </Stack>
                  </Stack>
                </ListItem>
              ))}
            </List>
          )}
          {!cardsLoading ? (
            <>
              <Divider sx={{ my: 2 }} />
              {showAddPanel ? (
                <Stack
                  spacing={1.5}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2}}
                >
                  <Typography variant="subtitle2">
                    Add a payment method
                  </Typography>
                  {addCardError ? (
                    <Alert severity="error" onClose={() => setAddCardError('')}>
                      {addCardError}
                    </Alert>
                  ) : null}
                  <Box
                    sx={{
                      backgroundColor: '#eef2f6',
                      p: 1.5,
                      borderRadius: 1}}
                  >
                    <Elements stripe={stripePromise}>
                      <PaymentForm
                        createToken={createToken}
                        setResetToken={setResetToken}
                      />
                    </Elements>
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={makeDefaultOnAdd}
                        onChange={(e) =>
                          setMakeDefaultOnAdd(e.target.checked)
                        }
                      />
                    }
                    label="Set as default payment method"
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary"
                    }}
                  >
                    By continuing, you agree to our{' '}
                    <a href="/Terms" target="_blank" rel="noreferrer">
                      Terms &amp; Conditions
                    </a>{' '}
                    and our{' '}
                    <a href="/Privacy" target="_blank" rel="noreferrer">
                      Privacy Policy
                    </a>
                    .
                  </Typography>
                  <Stack spacing={1}>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        boxShadow: "0 0.25rem 0.55rem rgba(65, 145, 255, 0.35)",
                        color: "#fff",
                        backgroundColor: "#4191ff",
                        backgroundImage: "none",
                        borderColor: "#4191ff",
                        "&:not(:disabled):hover": { backgroundColor: "#4191ff", color: "#fff" },
                        "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#0e73ff" },
                        "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(65, 145, 255, 0.4), 0 0.0625rem 0.385rem rgba(65, 145, 255, 0.54)" },
                        "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                      }}
                      disabled={!stripeToken || savingCard}
                      onClick={() => handleSaveCard({ chargeNow: true })}
                    >
                      {savingCard ? 'Saving…' : 'Save and charge now'}
                    </Button>
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}>
                      <Button
                        size="small"
                        variant="text"
                        disabled={savingCard}
                        onClick={closeAddPanel}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        disabled={!stripeToken || savingCard}
                        onClick={() => handleSaveCard({ chargeNow: false })}
                      >
                        Save card only
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              ) : (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setShowAddPanel(true)
                    setAddCardError('')
                  }}
                >
                  Add Payment Method
                </Button>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </BlockUi>
  );
}

export default withSession(DelinquentPageComponent)
